import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PrimaryButton } from "./SharedStyles";
import { T100 } from "./SharedStyles";
import I18n from "../../../translations/i18n";
import { numOfHotels } from "../../../settings/IntegrationConstants";

const Section = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const Header = styled.div`
  color: var(--Content-Static-Strong, #111415);
  text-align: center;
  font-feature-settings: 'salt' on, 'ss01' on;

  /* Heading/H400/Desktop */
  font-family: ${({ theme }) => theme.secondaryFont} !important;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 120% */
  letter-spacing: -0.1px;
  padding: 0 8px 8px;
  margin-top: -8px;
  text-wrap: balance;
`;

const Subheader = styled.div`
  color: var(--Content-Static-Strong, #111415);
  text-align: center;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 153.846% */
`;

const TwoSquareContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const Square = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
  border-radius: var(--Small, 8px);
  background: #FFF;

  /* Elevation/Main/0dp */
  box-shadow: 0 0.5px 0.25px 0 var(--neutral-darken-200-d, rgba(12, 34, 45, 0.09)), 0 0 16px 0 var(--neutral-darken-100-d, rgba(22, 22, 55, 0.03)), 0 0 1px 0 var(--neutral-darken-400-d, rgba(10, 27, 41, 0.29)) inset;
`;

const SquareHeader = styled.div`
  color: ${({ theme }) => theme.primaryColor} !important;
  text-align: center;
  font-feature-settings: 'salt' on, 'ss01' on;

  /* Heading/H500/Desktop */
  font-family: ${({ theme }) => theme.secondaryFont} !important;
  font-size: 26px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px; /* 123.077% */
  letter-spacing: -0.26px;
`;

const SquareSubheader = styled.div`
  color: ${({ theme }) => theme.primaryColor} !important;
  text-align: center;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px; /* 123.077% */
  letter-spacing: 0.065px;
`;

const SquareText = styled.div`
  color: var(--Content-Static-Default, #363D42);
  text-align: center;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px; /* 120% */
  letter-spacing: 0.05px;
`;


const L100 = styled.div`
  color: var(--Content-Static-Dull, #6F787F);
  text-align: center;
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 14px; /* 127.273% */
  letter-spacing: 0.055px;
`;

const BenefitsSection = styled.div<{ count: number }>`
  display: grid;
  gap: 8px;
  justify-content: center;
  padding: 8px;

  ${({ count }) =>
    count === 1 &&
    `
    grid-template-columns: 1fr;
    max-width: 300px;
    margin: 0 auto;
  `}

  ${({ count }) =>
    count === 2 &&
    `
    grid-template-columns: repeat(2, 1fr);
    max-width: 600px;
  `}

  ${({ count }) =>
    count === 3 &&
    `
    grid-template-columns: repeat(3, 1fr);
    max-width: 900px;
  `}

  ${({ count }) =>
    count === 4 &&
    `
    grid-template-columns: repeat(2, 1fr);
    max-width: 600px;
  `}
`;


const Reward = styled.div`
  margin: 8px 0px 4px !important;
  display: flex !important;
  font-size: 14px !important;
  line-height: 21px !important;
  align-items: center;
  justify-content: center;
`;


const RewardText = styled.div`
  color: ${({ theme }) => theme.primaryColor} !important;
  font-weight: 700 !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px;
`;

const IconContainer = styled.span`
  svg {
    path {
      fill: ${({ theme }) => theme.primaryColor} !important;
    }
    height: 32px !important;
    width: 32px !important; 
  }
`;

type Props = {
  onJoinClick: () => void;
  onLoginClick: () => void;
  benefitsCorrectLanguage: any[];
  userLoggedIn: boolean;
  widgetLoginEnabled: boolean;
  firstName: string;
  cobrandingEnabled: boolean;
  whiteLabel: string;

}

const IntroScreen = ({ onJoinClick, onLoginClick, benefitsCorrectLanguage, userLoggedIn, widgetLoginEnabled, firstName, cobrandingEnabled, whiteLabel }: Props) => {
  return (
      <>
        <Header>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.header`, { num_of_hotels: numOfHotels })}</Header>
        <Section>
          <Subheader>
           {
              userLoggedIn && firstName ? (
                `${firstName}, book today and earn your choice of:`
              ) : cobrandingEnabled ? (
                `Members of ${whiteLabel} earn their choice of:`
              ) : (
                "Members of The Guestbook earn their choice of:"
              )
            }
            </Subheader>
          <TwoSquareContainer>
            <Square>
              <SquareHeader>5%</SquareHeader>
              <SquareSubheader>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.cash_back`)}</SquareSubheader>
              <SquareText>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.cash_back_subtitle`)}</SquareText>
            </Square>
            <L100>or</L100>
            <Square>
              <SquareHeader>15%</SquareHeader>
              <SquareSubheader>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.cash_forward`)}</SquareSubheader>
              <SquareText>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.cash_forward_subtitle`)}</SquareText>
            </Square>
          </TwoSquareContainer>
        </Section>

        {benefitsCorrectLanguage?.length > 0 && 
          <Section>
            <Subheader>
              {I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.additional_benefits`, {hotel_name: window.gb_data.hotel.name})}</Subheader>
            <BenefitsSection count={benefitsCorrectLanguage?.length || 0}>
              {benefitsCorrectLanguage?.map((benefit: any, index: any) => {
                return (
                  <Reward key={index}>
                    <RewardText>
                      <IconContainer style={{ top: "0" }}>
                        <FontAwesomeIcon icon={benefit.icon_class} />
                      </IconContainer>
                      <T100>{benefit.title}</T100>
                    </RewardText>
                  </Reward>
                );
              })}
            </BenefitsSection>
          </Section>
        }
        
        {widgetLoginEnabled ? 
          !userLoggedIn && (
            <Section>
              <PrimaryButton onClick={onJoinClick}>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.buttons.join`)}</PrimaryButton>
              <T100>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.already_member`)}{" "}
                <span onClick={onLoginClick}>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.buttons.login`)}</span>
              </T100>
            </Section>
          )
          :
          <T100>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.not_member`)}<br></br>{I18n.t(`views.hotel_integration.components.widget.mobile_modal.intro.join`)}</T100>
        }
  
    </>
  );
};

export default IntroScreen;
